"use client";

import { useSectionView } from "@/app/utils/analytics";
import { handleCheckoutSession } from "@/app/utils/checkout-session";
import { sans, mono } from "@/app/utils/fonts";
import { DISCOUNT_ID, PRICING_ID_T1, PRICING_NAME_T1 } from "@/lib/constants";
import { motion } from "motion/react";

const COSTS: [string, string][] = [
  ["6 hrs", "designing the extension UI"],
  ["4 hrs", "wiring Stripe payments + webhooks"],
  ["1 day", "setting up Google OAuth"],
  ["2 hrs", "protecting your API routes"],
  ["2 weeks", "getting rejected in Web Store review"],
  ["∞ hrs", "overthinking all of it"],
];

export default function SaveHours() {
  const sectionRef = useSectionView("value_prop");
  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden bg-ink"
      id="side-by-side"
    >
      {/* atmosphere */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-8 h-72 w-[700px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(11,99,222,0.12),transparent_70%)] blur-2xl" />
      </div>

      <motion.div className="mx-auto flex w-full max-w-6xl flex-col items-center px-6 py-24">
        {/* badge */}
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-line bg-panel/70 px-4 py-1.5 text-sm font-medium text-zinc-300 shadow-sm backdrop-blur">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-primary"
          >
            <path d="M5 22h14" />
            <path d="M5 2h14" />
            <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
            <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
          </svg>
          Save 60+ hours
        </div>

        {/* heading */}
        <h2
          className={`${sans.className} max-w-3xl text-center text-4xl font-medium leading-[1.05] tracking-tight text-white md:text-5xl`}
        >
          <span className="text-primary">Ready in minutes.</span> Skip{" "}
          <span className="bg-gradient-to-br from-[#4d94ff] via-primary to-[#0a4fb0] bg-clip-text text-transparent">
            60+ hours
          </span>{" "}
          of setup and start earning.
        </h2>

        {/* cost card */}
        <div className="relative mt-12 w-full max-w-lg">
          <div className="absolute -inset-3 -z-10 rounded-3xl bg-[radial-gradient(ellipse_at_center,rgba(11,99,222,0.16),transparent_70%)] blur-2xl" />
          <div className="overflow-hidden rounded-2xl border border-line bg-panel p-7 shadow-2xl shadow-black/30 ring-1 ring-white/5">
            <p className="mb-6 text-xs font-medium uppercase tracking-[0.18em] text-zinc-500">
              Building it yourself
            </p>

            <div className="flex flex-col gap-4">
              {COSTS.map(([time, label]) => (
                <div
                  key={label}
                  className="flex items-baseline gap-3 text-[15px] text-zinc-300"
                >
                  <span
                    className={`${mono.className} w-[68px] shrink-0 font-semibold text-red-400`}
                  >
                    +{time}
                  </span>
                  <span>{label}</span>
                </div>
              ))}
            </div>

            <div className="my-6 h-px bg-white/10" />

            <p className={`${sans.className} text-3xl font-bold text-white`}>
              = <span className="text-red-400">60+ hours</span> before your first
              dollar
            </p>
            <p className="mt-2 text-zinc-400">
              Skip straight to the part where you make money.
            </p>

            <button
              onClick={() =>
                handleCheckoutSession(
                  PRICING_ID_T1,
                  DISCOUNT_ID,
                  PRICING_NAME_T1,
                  "save_hours",
                )
              }
              className="mt-7 w-full rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-white ring-1 ring-inset ring-white/15 shadow-[0_10px_36px_-10px_rgba(11,99,222,0.6)] transition-all duration-200 hover:bg-secondary active:scale-[0.98]"
            >
              Skip the setup →
            </button>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
